import { nonEmptyArray } from '@ipfabric/utils';
import { propOf } from '@ipfabric/utils/fp/prop';
import { getSecurityMachineFunctionByType } from 'components/SecuritySchema/helpers/getSecurityMachineFunctionByType';
import type {
  SecuritySchemaBreadcrumb,
  SecuritySchemaCommandFactory,
} from 'components/SecuritySchema/model/SecuritySchemaModel';
import {
  type SecuritySchemaScope,
  isListingScope,
  isRuleChainScope,
} from 'components/SecuritySchema/model/SecuritySchemaScope';
import { getRuleChainInitialPage } from 'components/SecuritySchema/model/helpers/getRuleChainInitialPage';
import { ruleChainAncestors } from 'components/SecuritySchema/model/helpers/ruleChainAncestors';
import { selectVisibleRuleChainItems } from 'components/SecuritySchema/model/selectors/selectVisibleRuleChainItems';
import { flow } from 'fp-ts/lib/function';

interface OpenSecuritySchemaScopeCommand {
  scope: SecuritySchemaScope;
}

export const openSecuritySchemaScope: SecuritySchemaCommandFactory<
  OpenSecuritySchemaScopeCommand
> = (cmdContext) => {
  /**
   * When listing scope with no entity type is encountered, select first entity type
   * with some rule chains to be auto-selected if available.
   *
   * Note: at the time of writing this code, there is no scenario that uses it
   * (we meant it for clicking Hostname breadcrumb, but that has shown to behave weirdly).
   * But it seems reasonable if we add e.g. some feature to open device security from outside
   * without particular rule scope, so let's keep it here.
   */
  const injectEntityTypeToListingScope = (scope: SecuritySchemaScope) => {
    if (!isListingScope(scope) || scope.entityType !== undefined) {
      return scope;
    }
    const firstNonEmptyTab = (['acl', 'zoneFw'] as const).find(
      flow(
        propOf(selectVisibleRuleChainItems(cmdContext.getState())),
        nonEmptyArray.isNonEmpty,
      ),
    );
    if (!firstNonEmptyTab) {
      return scope;
    }
    return {
      ...scope,
      entityType: firstNonEmptyTab,
    };
  };

  /**
   * The trail down to `scope`, so a chain opened from the entity list reads the
   * same as one reached from a decision's rule path. Empty unless another chain
   * `exec`s it — a GCP policy chain under its enforcement point (NIM-24555).
   */
  const breadcrumbsForScope = (
    scope: SecuritySchemaScope,
  ): SecuritySchemaBreadcrumb[] => {
    const { deviceSecurity } = cmdContext.getState();
    if (!isRuleChainScope(scope) || deviceSecurity === null) {
      return [];
    }
    const machine =
      getSecurityMachineFunctionByType[scope.securityType](deviceSecurity);
    if (machine === null) {
      return [];
    }
    return ruleChainAncestors(machine, scope.name).map((ruleChainName) => ({
      ruleChainName,
      securityType: scope.securityType,
    }));
  };

  return (command) => {
    cmdContext.updateState((draft) => {
      const scope = injectEntityTypeToListingScope(command.scope);
      draft.breadcrumbs = breadcrumbsForScope(scope);
      draft.scope = scope;
      draft.page = getRuleChainInitialPage(draft);
    });
  };
};
